import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token expiry
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
};

// Courses
export const coursesAPI = {
  getAll: (params) => api.get('/courses', { params }),
  getById: (id) => api.get(`/courses/${id}`),
  create: (data) => api.post('/courses', data),
  update: (id, data) => api.put(`/courses/${id}`, data),
  delete: (id) => api.delete(`/courses/${id}`),
};

// Enrollments
export const enrollmentsAPI = {
  getAll: () => api.get('/enrollments'),
  create: (data) => api.post('/enrollments', data),
  updateProgress: (id, data) => api.put(`/enrollments/${id}/progress`, data),
};

// Learning Paths
export const learningPathsAPI = {
  getAll: () => api.get('/learning-paths'),
  getById: (id) => api.get(`/learning-paths/${id}`),
  create: (data) => api.post('/learning-paths', data),
};

// Quizzes
export const quizzesAPI = {
  getById: (id) => api.get(`/quizzes/${id}`),
  create: (data) => api.post('/quizzes', data),
  submitAttempt: (id, data) => api.post(`/quizzes/${id}/attempt`, data),
};

// Gamification
export const gamificationAPI = {
  getLeaderboard: (params) => api.get('/leaderboard', { params }),
  getBadges: () => api.get('/badges'),
  getAchievements: () => api.get('/achievements'),
  getPointsHistory: () => api.get('/points/history'),
};

// Discussions
export const discussionsAPI = {
  getAll: (params) => api.get('/discussions', { params }),
  create: (data) => api.post('/discussions', data),
  getComments: (id) => api.get(`/discussions/${id}/comments`),
  addComment: (id, data) => api.post(`/discussions/${id}/comments`, data),
};

// Recognitions
export const recognitionsAPI = {
  getAll: () => api.get('/recognitions'),
  create: (data) => api.post('/recognitions', data),
};

// Compliance
export const complianceAPI = {
  getStatus: () => api.get('/compliance'),
};

// Analytics
export const analyticsAPI = {
  getDashboard: () => api.get('/analytics/dashboard'),
  getManager: () => api.get('/analytics/manager'),
};

// Categories
export const categoriesAPI = {
  getAll: () => api.get('/categories'),
};

// Stores
export const storesAPI = {
  getAll: () => api.get('/stores'),
  create: (data) => api.post('/stores', data),
};

// Users
export const usersAPI = {
  search: (params) => api.get('/users', { params }),
};

// Seed
export const seedAPI = {
  seed: () => api.post('/seed'),
};

// Settings
export const settingsAPI = {
  getSMTP: () => api.get('/settings/smtp'),
  saveSMTP: (data) => api.post('/settings/smtp', data),
  testSMTP: (data) => api.post('/settings/smtp/test', data),
  getWhatsApp: () => api.get('/settings/whatsapp'),
  saveWhatsApp: (data) => api.post('/settings/whatsapp', data),
  getHRIntegrations: () => api.get('/settings/hr-integrations'),
  addHRIntegration: (data) => api.post('/settings/hr-integrations', data),
  deleteHRIntegration: (platform) => api.delete(`/settings/hr-integrations/${platform}`),
  syncHR: (platform) => api.post(`/settings/hr-integrations/${platform}/sync`),
  getHRLogs: (platform) => api.get(`/settings/hr-integrations/${platform}/logs`),
};

// Notifications
export const notificationsAPI = {
  getAll: () => api.get('/notifications'),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
  create: (data) => api.post('/notifications', data),
};

// Share
export const shareAPI = {
  getAchievementShare: (badgeId) => api.get(`/share/achievement/${badgeId}`),
  getCourseCompletionShare: (courseId) => api.get(`/share/course-completion/${courseId}`),
};

export default api;
